import { IconArrowNarrowRight } from "@tabler/icons-react";
import { EntityPickerField } from "molecules/entity-picker/EntityPickerField";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router";
import { useLastActiveContext } from "src/common/utils/hooks/useLastActiveContext.js";
import { ALL_LEGAL_ENTITIES } from "src/common/utils/lastActiveContext.js";
import {
	useLoadedCompanyDomain,
	useLoadedLegalEntities,
} from "src/routes/routeDataHooks.js";
import { useRouteTree } from "src/routes/routeTreeContext.js";
import { buildScopedNavigationPath } from "./scopedFeaturePaths.js";
import { SCOPE_SWITCH_STATE } from "src/routes/scopeChange.js";
import { DOMAIN_TERMS } from "src/common/domainTerms.js";

export const LegalEntitySelect = () => {
	const { t } = useTranslation();
	const companyDomain = useLoadedCompanyDomain();
	const legalEntities = useLoadedLegalEntities();
	const { write: writeLastActiveContext } = useLastActiveContext();
	const { pathname } = useLocation();
	const { routeObjects, gatedFeatureSegments } = useRouteTree();
	const { legalEntityId } = useParams();
	const currentLegalEntity = legalEntities.find(
		(legalEntity) => legalEntity.id === legalEntityId,
	);

	const items = useMemo(() => {
		const legalEntityList = legalEntities
			.filter((legalEntity) => legalEntity.id !== legalEntityId)
			.map((legalEntity) => ({ id: legalEntity.id, name: legalEntity.name }));
		legalEntityList.sort((a, b) => a.name.localeCompare(b.name));

		// Already on the domain-wide view — nothing to pick between.
		if (legalEntityId === undefined) {
			return legalEntityList;
		}
		return [
			{ id: ALL_LEGAL_ENTITIES, name: t("All companies") },
			...legalEntityList,
		];
	}, [legalEntities, legalEntityId, t]);

	// WHY rendered with nothing to pick: a vanished picker hides a misconfigured account.
	const hasCompanies = legalEntities.length > 0;

	return (
		<EntityPickerField
			data-experience-anchor="company-picker"
			data-testid="legal-entity-select"
			interactive={hasCompanies}
			pillLabel={t(DOMAIN_TERMS.COMPANY)}
			currentLabel={
				hasCompanies
					? (currentLegalEntity?.name ?? t("All companies"))
					: t("No companies")
			}
			items={items}
			searchPlaceholder={t("Search legal entities")}
			renderItem={(item, { close }) => {
				const isAllCompanies = item.id === ALL_LEGAL_ENTITIES;
				return (
					<Link
						to={buildScopedNavigationPath({
							pathname,
							companyDomainId: companyDomain.id,
							legalEntityId: isAllCompanies ? undefined : item.id,
							routeObjects,
							gatedFeatureSegments,
						})}
						state={SCOPE_SWITCH_STATE}
						className="group flex items-center gap-2 border-b px-3 py-2 text-gray-700 last:border-b-0 hover:bg-gray-100"
						onClick={() => {
							writeLastActiveContext({
								companyDomainId: companyDomain.id,
								legalEntityId: isAllCompanies ? ALL_LEGAL_ENTITIES : item.id,
							});
							close();
						}}
					>
						<div className="grow truncate">{item.name}</div>
						<IconArrowNarrowRight
							className="shrink-0 text-gray-500 opacity-0 group-hover:opacity-100"
							size={16}
							strokeWidth={1.5}
						/>
					</Link>
				);
			}}
		/>
	);
};
